// The deployment's flavour on screen (backend/modules/flavour.py, GET /api/platform/brand): its name, logo, colours,
// sign-in words, menu labels and a few switches. Read once at start; the last answer is kept so the next load draws
// the right brand before the server answers. Nothing here decides access: the server does that on every request.

export const BRAND_CACHE_KEY = 'foodops_brand_v1';

// The vendor flavour (backend/modules/flavours/vendor.yaml): what the screens show until the server answers, and if
// it never does.
export const DEFAULT_BRAND = {
  id: 'vendor',
  brand: {
    product_name: 'FoodOps',
    short_name: 'FoodOps',
    tagline: 'Invoices, stock and traceability for food businesses',
    icon_letter: 'F',
    logo_url: null,
    powered_by: false,
  },
  theme: { primary: '#1E3A2F', accent: '#D96C4A', background: '#F9F7F3', css_vars: {} },
  landing: { visitor: 'marketing', signin_title: 'Welcome back', signin_subtitle: 'Sign in to your account' },
  labels: {},
  features: { plan_badge: true, demo_controls: true, mobile_offer: true },
};

/** A server answer laid over the defaults, so a missing section never leaves a screen without a value. */
export function mergeBrand(answer) {
  const a = answer && typeof answer === 'object' ? answer : {};
  return {
    ...DEFAULT_BRAND,
    ...a,
    brand: { ...DEFAULT_BRAND.brand, ...(a.brand || {}) },
    theme: { ...DEFAULT_BRAND.theme, ...(a.theme || {}) },
    landing: { ...DEFAULT_BRAND.landing, ...(a.landing || {}) },
    labels: { ...(a.labels || {}) },
    features: { ...DEFAULT_BRAND.features, ...(a.features || {}) },
  };
}

/** The label a flavour gives a screen ("nav-cut-cost-inventory") or a workspace ("workspace:cut-cost"). */
export function labelFor(brand, id, fallback) {
  const v = brand && brand.labels ? brand.labels[id] : undefined;
  return typeof v === 'string' && v.trim() ? v : fallback;
}

/** Colours, title, icon and theme colour onto the page. Only CSS custom properties the server named are set. */
export function applyBrand(brand, doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || !brand) return;
  const root = doc.documentElement;
  Object.entries((brand.theme && brand.theme.css_vars) || {}).forEach(([k, v]) => {
    if (/^--color-[a-z]+(-[a-z0-9]+)*$/.test(k) && /^\d{1,3} \d{1,3} \d{1,3}$/.test(String(v))) {
      root.style.setProperty(k, v);
    }
  });
  if (brand.brand && brand.brand.product_name) doc.title = brand.brand.product_name;
  const theme = doc.querySelector('meta[name="theme-color"]');
  if (theme && brand.theme && brand.theme.primary) theme.setAttribute('content', brand.theme.primary);
  const apple = doc.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (apple && brand.brand && brand.brand.short_name) apple.setAttribute('content', brand.brand.short_name);
  if (brand.brand && brand.brand.logo_url) {
    doc.querySelectorAll('link[rel="icon"]').forEach((l) => {
      l.removeAttribute('type');            // the logo may be a PNG where the default icon is an SVG
      l.setAttribute('href', brand.brand.logo_url);
    });
  }
}

export function readCachedBrand(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  try {
    const raw = storage && storage.getItem(BRAND_CACHE_KEY);
    return raw ? mergeBrand(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function writeCachedBrand(answer, storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  try {
    if (storage) storage.setItem(BRAND_CACHE_KEY, JSON.stringify(answer));
  } catch { /* storage off: the next load draws the defaults first */ }
}
